import { SheetContentId, SheetContentLoadedEvent } from './CouponsAndVouchersSheet';
import { Action } from '@otto-ec/tracking-bct';
import { reloadVouchersSheet } from './reloadVouchersSheet';
import { Reloader } from '../basket-checkout-common/Reloader';
import { Tracking } from '../shared/Tracking';
import { onSheetOpen } from './SheetOpenEvents';

export class CompensationCodes {
    static COMPENSATION_CODE_NOTE_UNKNOWN = 'Der Gutschein steht nicht mehr zur Verfügung.';
    static COMPENSATION_CODE_NOTE_TECHNICAL = 'Es liegt derzeit ein technisches Problem vor. Bitte versuche es später erneut.';
    static COMPENSATION_CODE_NOTE_LOGGED_OUT = 'Aus Sicherheitsgründen haben wir dich abgemeldet. Bitte melde dich neu an um Gutscheine einzulösen.';

    constructor(
        private readonly reloader: Reloader,
        private readonly tracking: Tracking,
        private readonly showError: (message: string) => void
    ) {}

    initialize() {
        this._subscribeToContentLoadedEvent();
    }

    private _subscribeToContentLoadedEvent() {
        onSheetOpen((event: SheetContentLoadedEvent) => {
            if (event.id === SheetContentId.VOUCHERS) {
                this._initialize();
            }
        });
    }

    _initialize = () => {
        document.querySelectorAll('.or_js_compensationCodeActivateButton').forEach(button => {
            button.addEventListener('click', e => this.handleActivation(e));
        });
        document.querySelectorAll('.or_js_compensationCode_remove').forEach(button => {
            button.addEventListener('click', e => this.handleDeactivation(e));
        });
    };

    private async handleActivation(e: Event) {
        e.preventDefault();

        const target = e.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }
        const parentReductionItem = target.closest('.or_js_compensationCode') as HTMLElement | null;
        const compensationCodeId = parentReductionItem?.dataset.reductionId;
        if (!compensationCodeId) {
            console.error('No compensation code ID found');
            return;
        }

        this._toLoadingState(undefined, target);
        try {
            const response: Response = await this.activate(compensationCodeId);
            if (response.ok) {
                await this.handleActivationSuccess(compensationCodeId);
            } else {
                console.error('compensation code activation failed', response);
                await this.handleActivationFailure(response, compensationCodeId);
                this._toDefaultState(undefined, target);
            }
        } catch (err) {
            console.error('activation failed', err);
            await this.handleActivationFailure(null, compensationCodeId).catch(console.error);
            this._toDefaultState(undefined, target);
        }
    }

    private async handleDeactivation(e: Event) {
        const button = e.target as HTMLButtonElement | null;
        if (!button) {
            return;
        }
        const parentReductionItem = button.closest('.or_js_compensationCode') as HTMLElement | null;
        const compensationCodeId = parentReductionItem?.dataset.reductionId;
        if (!compensationCodeId) {
            console.error('No compensation code ID found');
            return;
        }
        try {
            const response: Response = await this.deactivate(compensationCodeId);
            if (response.ok) {
                await this.handleDeactivationSuccess(button, compensationCodeId);
            } else {
                await this.handleDeactivationFailure(response);
            }
        } catch (err) {
            console.error('deactivation failed', err);
            await this.handleDeactivationFailure(null).catch(console.error);
        }
    }

    private async handleActivationFailure(response: Response | null, compensationCodeId: string) {
        let reloadSheet: boolean = false;
        let errorKey: string;
        switch (response?.status) {
            case 422:
                const body = (await response.json()) as ErrorResponse;
                errorKey = body.errorKey;
                reloadSheet = true;
                break;
            case 401:
                errorKey = 'compensationCode.activation.logged-out';
                break;
            default:
                errorKey = 'compensationCode.activation.technical-error';
        }

        if (reloadSheet) {
            reloadVouchersSheet();
        }
        this.showActivationError(errorKey);
        this.trackActivationFailure(errorKey, compensationCodeId);
    }

    private async handleDeactivationFailure(response: Response | null) {
        let errorKey: string;
        switch (response?.status) {
            case 422:
                const body = (await response.json()) as ErrorResponse;
                errorKey = body.errorKey;
                break;
            case 401:
                errorKey = 'compensationCode.activation.logged-out';
                break;
            default:
                errorKey = 'compensationCode.activation.technical-error';
        }
        this.showActivationError(errorKey);
    }

    private _toLoadingState(input: HTMLElement | undefined, button: HTMLElement) {
        input?.setAttribute('disabled', '');
        button.setAttribute('disabled', '');
        button.setAttribute('loading', '');
    }

    private _toDefaultState(input: HTMLElement | undefined, button: HTMLElement) {
        input?.removeAttribute('disabled');
        button.removeAttribute('disabled');
        button.removeAttribute('loading');
    }

    private showActivationError(errorKey: string) {
        const errorKeyToHintMap: Record<string, string> = {
            'compensationCode.activation.logged-out': CompensationCodes.COMPENSATION_CODE_NOTE_LOGGED_OUT,
            'compensationCode.activation.unknown': CompensationCodes.COMPENSATION_CODE_NOTE_UNKNOWN,
            'compensationCode.activation.technical-error': CompensationCodes.COMPENSATION_CODE_NOTE_TECHNICAL
        };
        this.showError(errorKeyToHintMap[errorKey] || CompensationCodes.COMPENSATION_CODE_NOTE_TECHNICAL);
    }

    private activate(compensationCodeId: string) {
        return fetch(`/order/basket/api/compensation-code/activate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            redirect: 'error',
            body: JSON.stringify({ id: compensationCodeId })
        });
    }

    private deactivate(compensationCodeId: string) {
        return fetch(`/order/basket/api/compensation-code/deactivate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            redirect: 'error',
            body: JSON.stringify({ id: compensationCodeId })
        });
    }

    private async handleActivationSuccess(compensationCodeId: string) {
        await this.reloader.trigger();

        this.trackActivationSuccess(compensationCodeId);
        reloadVouchersSheet();
    }

    private async handleDeactivationSuccess(button: HTMLElement, compensationCodeId: string) {
        await this.reloader.trigger();

        this.trackDeactivationSuccess(compensationCodeId);
        reloadVouchersSheet();

        /*                       */
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('activated', 'true');
    }

    private trackActivationSuccess(compensationCodeId: string) {
        const action: Action = {
            name: 'select',
            features: [
                {
                    id: 'basket_reduction_submitted',
                    name: 'BasketReduction',
                    status: 'selected',
                    labels: {
                        order_ReductionType: ['compensationCode'],
                        order_ReductionCompensationCodeId: [compensationCodeId]
                    }
                }
            ]
        };

        this.tracking.submitEventMerge(action);
    }

    private trackActivationFailure(errorKey: string, compensationCodeId: string) {
        const action: Action = {
            name: 'select',
            features: [
                {
                    id: 'basket_reduction_submitted',
                    name: 'BasketReduction',
                    status: 'error',
                    labels: {
                        order_ReductionType: ['compensationCode'],
                        order_ReductionCompensationCodeId: [compensationCodeId]
                    }
                },
                {
                    id: 'order_message',
                    name: 'OrderMessage',
                    status: 'visible',
                    labels: {
                        order_ErrorMessage: [errorKey]
                    }
                }
            ]
        };
        this.tracking.submitEventMerge(action, true);
    }

    private trackDeactivationSuccess(compensationCodeId: string) {
        const action: Action = {
            name: 'deselect',
            features: [
                {
                    id: 'basket_reduction_submitted',
                    name: 'BasketReduction',
                    status: 'deselected',
                    labels: {
                        order_ReductionType: ['compensationCode'],
                        order_ReductionCompensationCodeId: [compensationCodeId]
                    }
                }
            ]
        };

        this.tracking.submitEventMerge(action);
    }
}

export interface ErrorResponse {
    errorKey: string;
}
